import * as ApiResponse from "./responses";
import { ErrorObject } from "ajv";
import { i18n } from "@/locales";

export class ApiClient {
  private bearerToken = "";
  private apiUrl: string = import.meta.env.VITE_API_URL;

  private static instance: ApiClient;
  private constructor() {
    //
  }

  public static getInstance(): ApiClient {
    if (!ApiClient.instance) ApiClient.instance = new ApiClient();

    return ApiClient.instance;
  }

  public setBearerToken(_bearerToken: string): void {
    this.bearerToken = _bearerToken;
  }

  public async getSystemList(): Promise<ApiResponse.Systems.GetSystems> {
    const requestUrl = `${this.apiUrl}/systems`;
    //request
    let request;
    try {
      request = await this.fetchBody(requestUrl, {
        headers: {
          Authorization: `Bearer ${this.bearerToken}`,
        },
      });

      //body type check
      if (!ApiResponse.Systems.validate(request.body)) {
        throw new ResponseParseError(JSON.stringify(ApiResponse.Systems.validate.errors));
      }
    } catch (error) {
      throw this.requestError(i18n.global.t("app.system.info.list"), error);
    }

    return request.body;
  }

  public async getSupplierList(supplierId: string, system: string): Promise<ApiResponse.Suppliers.GetSuppliers> {
    const requestUrl = `${this.apiUrl}/suppliers/${supplierId}?system=${system}`;

    //request
    let request;
    try {
      request = await this.fetchBody(requestUrl, {
        headers: {
          Authorization: `Bearer ${this.bearerToken}`,
        },
      });

      //body type check
      if (!ApiResponse.Suppliers.validate(request.body)) {
        throw new ResponseParseError(JSON.stringify(ApiResponse.Suppliers.validate.errors));
      }
    } catch (error) {
      throw this.requestError(i18n.global.t("app.supplier.info.list"), error);
    }

    return request.body;
  }

  public async validateMaterial(
    materialId: string,
    supplierId: string,
    originalSupplierId: string,
    system: string,
    signal?: AbortSignal
  ): Promise<boolean> {
    const requestUrl = `${this.apiUrl}/suppliers/${supplierId}/material/${materialId}?originalSupplierId=${originalSupplierId}&system=${system}`;

    //request
    let response;
    try {
      response = await fetch(requestUrl, {
        headers: {
          Authorization: `Bearer ${this.bearerToken}`,
        },
        signal,
      });
    } catch (error) {
      if (error instanceof Error && error?.name == "AbortError") throw error;
      throw this.requestError(i18n.global.t("app.material.info.check"), error);
    }

    if (response.status == 200) return true;

    return false;
  }

  public async getLabelList(
    materialId: string,
    supplierId: string,
    originalSupplierId: string,
    system: string,
    signal?: AbortSignal
  ): Promise<ApiResponse.PrintItems.GetPrintItems> {
    const requestUrl = `${this.apiUrl}/suppliers/${supplierId}/material/${materialId}/printitems?originalSupplierId=${originalSupplierId}&system=${system}&language=de`;
    let request;
    //request
    try {
      request = await this.fetchBody(requestUrl, {
        headers: {
          Authorization: `Bearer ${this.bearerToken}`,
        },
        signal,
      });

      //body type check
      if (!ApiResponse.PrintItems.validate(request.body)) {
        throw new ResponseParseError(JSON.stringify(ApiResponse.PrintItems.validate.errors));
      }
    } catch (error) {
      if (error instanceof Error && error?.name == "AbortError") throw error;

      throw this.requestError(i18n.global.t("app.printLabels.info.name"), error);
    }

    return request.body;
  }

  public async getPreviewImage(
    materialId: string,
    supplierId: string,
    originalSupplierId: string,
    system: string,
    layout: string,
    printData: string
  ): Promise<Blob> {
    const requestUrl = `${this.apiUrl}/suppliers/${supplierId}/material/${materialId}/printitem/image?system=${system}&originalSupplierId=${originalSupplierId}`;
    let response;
    try {
      response = await fetch(requestUrl, {
        method: "post",
        headers: {
          Authorization: `Bearer ${this.bearerToken}`,
          "content-Type": "application/json",
        },
        body: JSON.stringify({
          originalSupplierId: originalSupplierId,
          system: system,
          layout: layout,
          printData: printData,
        }),
      });
    } catch (error) {
      throw this.requestError(i18n.global.t("app.printLabels.info.preview"), error);
    }
    if (response.status < 200 || response.status > 299) throw new Error("Invalid response status code");

    let blob;
    try {
      blob = await response.blob();
    } catch (error) {
      throw new Error(
        `Failed to get BLOB of request ${requestUrl}. Details: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
    return blob;
  }

  public async downloadGeneratedLabel(
    materialId: string,
    supplierId: string,
    quantity: string,
    layout: string,
    printData: string,
    originalSupplierId: string,
    system: string,
    date: string
  ): Promise<void> {
    let data;
    let response;
    try {
      response = await fetch(
        `${this.apiUrl}/suppliers/${supplierId}/material/${materialId}/printitem/pdf?system=${system}&originalSupplierId=${originalSupplierId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${this.bearerToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            quantity: quantity,
            layout: layout,
            printData: printData,
            originalSupplierId: originalSupplierId,
            system: system,
          }),
        }
      );
    } catch (error) {
      throw new Error("Request failed");
    }

    if (response.status < 200 || response.status > 299) throw new Error("Bad response status");

    try {
      data = await response.blob();
    } catch (error) {
      throw new Error("Failed to parse response");
    }
    if (data.size === 0) throw new Error("Response is empty");

    const url = window.URL.createObjectURL(data);
    const link = document.createElement("a");
    link.href = url;
    const fileName = `LABEL_${materialId}_${layout}(x${quantity})_${date}.pdf`;
    const sanitizedFileName = fileName.replaceAll(/[/\\:*?"'<>|\0]+/g, "_");
    link.setAttribute("download", sanitizedFileName);
    document.body.appendChild(link);
    link.click();
  }

  public async getPdfHistory(
    materialId: string,
    supplierId: string,
    originalSupplierId: string,
    system: string,
    startDate: string,
    signal?: AbortSignal
  ): Promise<ApiResponse.PrintItems.GetPrintItemsPdf> {
    startDate = startDate != null && startDate != "" ? startDate : "2018-01-01T00:00:00";

    const requestUrl = `${this.apiUrl}/suppliers/${supplierId}/material/${materialId}/printitems/pdf?system=${system}&originalSupplierId=${originalSupplierId}&startDate=${startDate}`;
    //request
    let request;
    try {
      request = await this.fetchBody(requestUrl, {
        headers: {
          Authorization: `Bearer ${this.bearerToken}`,
        },
        signal,
      });
    } catch (error) {
      if (error instanceof Error && error?.name == "AbortError") throw error;

      // not throwing because the api returns status 500 on empty pdf list (i don't know why)
      if (error instanceof RequestError) {
        return [];
      }

      throw this.requestError(i18n.global.t("app.pdfHistory.info.name"), error);
    }

    try {
      //body type check
      if (!ApiResponse.PrintItems.validatePdf(request.body)) {
        throw new ResponseParseError(JSON.stringify(ApiResponse.PrintItems.validatePdf.errors));
      }
    } catch (error) {
      throw this.requestError(i18n.global.t("app.pdfHistory.info.name"), error);
    }

    return request.body;
  }

  public async downloadPdfHistoryFile(
    materialId: string,
    supplierId: string,
    originalSupplierId: string,
    layout: string,
    system: string,
    printItemId: string,
    date: string,
    quantity: number
  ): Promise<void> {
    let blobData;
    let response;
    try {
      response = await fetch(
        `${this.apiUrl}/suppliers/${supplierId}/material/${materialId}/printitem/pdf/${printItemId}?system=${system}&originalSupplierId=${originalSupplierId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${this.bearerToken}`,
          },
        }
      );
    } catch (error) {
      throw new Error("Request failed");
    }
    try {
      blobData = (await response.blob()) as Blob;
    } catch (error) {
      throw new Error("Request parsing failed");
    }
    if (blobData.size === 0) throw new Error("Blob is zero");
    if (response.status < 200 || response.status > 299) throw new Error("Invalid response status code");

    const url = window.URL.createObjectURL(blobData);
    const link = document.createElement("a");
    link.href = url;
    const fileName = `LABEL_${materialId}_${layout}(x${quantity})_${date}.pdf`;
    const sanitizedFileName = fileName.replaceAll(/[/\\:*?"'<>|\0]+/g, "_");
    link.setAttribute("download", sanitizedFileName);
    document.body.appendChild(link);
    link.click();
  }
  public async getCsvFile(params: {
    supplier: ApiResponse.Suppliers.Supplier;
    system: ApiResponse.Systems.SystemId;
  }): Promise<void> {
    await new Promise((resolve) => {
      setTimeout(() => {
        resolve(0);
      }, 1000);
    });

    // let blobData;
    // let response;
    // try {
    //   response = await fetch(
    //     `${this.apiUrl}/suppliers/${supplierId}/material/${materialId}/printitem/...`,
    //     {
    //       method: "GET",
    //       headers: {
    //         Authorization: `Bearer ${this.bearerToken}`,
    //       },
    //     }
    //   );
    // } catch (error) {
    //   throw new Error("Failed to get Data");
    // }
    // if (response.status < 200 || response.status > 299)
    // throw new Error("Bad response status");
    // try {
    //   blobData = await response.blob();
    // } catch (error) {
    // throw new Error("Failed to parse response");
    // }

    // const url = window.URL.createObjectURL(blobData);
    const url = window.URL.createObjectURL(new Blob());
    const link = document.createElement("a");
    link.href = url;
    const fileName = `PRINTDATA_${params.supplier.SupplierId}_${
      params.system.system
    }_${new Date().toLocaleDateString()}.csv`;
    const sanitizedFileName = fileName.replaceAll(/[/\\:*?"'<>|\0]+/g, "_");
    link.setAttribute("download", sanitizedFileName);
    document.body.appendChild(link);
    link.click();
  }

  public async getSerialSetByDate(
    materialId: string,
    supplierId: string,
    originalSupplierId: string,
    system: string,
    startDate: string | null,
    endDate: string | null,
    signal?: AbortSignal
  ): Promise<ApiResponse.Serials.GetSerials> {
    const requestUrl = `${this.apiUrl}/suppliers/${supplierId}/material/${materialId}/serials?startDate=${startDate}&endDate=${endDate}&originalSupplierId=${originalSupplierId}&system=${system}`;
    let request;
    //request
    try {
      request = await this.fetchBody(requestUrl, {
        headers: {
          Authorization: `Bearer ${this.bearerToken}`,
        },
        signal,
      });

      //body type check
      if (!ApiResponse.Serials.validate(request.body)) {
        throw new ResponseParseError(JSON.stringify(ApiResponse.Serials.validate.errors));
      }
    } catch (error) {
      if (error instanceof Error && error?.name == "AbortError") throw error;

      throw this.requestError(i18n.global.t("app.cancelSerials.info.list"), error);
    }

    return request.body;
  }

  public async getSerialSetByNumber(
    materialId: string,
    supplierId: string,
    originalSupplierId: string,
    system: string,
    serialNumber: string | null,
    signal?: AbortSignal
  ): Promise<ApiResponse.Serials.GetSerials> {
    const requestUrl = `${this.apiUrl}/suppliers/${supplierId}/material/${materialId}/serials?searchId=${serialNumber}&originalSupplierId=${originalSupplierId}&system=${system}`;
    //request
    let request;
    try {
      request = await this.fetchBody(requestUrl, {
        headers: {
          Authorization: `Bearer ${this.bearerToken}`,
        },
        signal,
      });

      //body type check
      if (!ApiResponse.Serials.validate(request.body)) {
        throw new ResponseParseError(JSON.stringify(ApiResponse.Serials.validate.errors));
      }
    } catch (error) {
      if (error instanceof Error && error?.name == "AbortError") throw error;

      throw this.requestError(i18n.global.t("app.cancelSerials.info.list"), error);
    }

    return request.body;
  }

  public async cancelSerials(
    materialId: string,
    supplierId: string,
    originalSupplierId: string,
    system: string,
    serialNumbers: Array<string>
  ): Promise<boolean> {
    const requestUrl = `${this.apiUrl}/suppliers/${supplierId}/material/${materialId}/serials?originalSupplierId=${originalSupplierId}&system=${system}`;
    let response;
    try {
      response = await fetch(requestUrl, {
        method: "put",
        headers: {
          Authorization: `Bearer ${this.bearerToken}`,
          "content-type": "application/json",
        },
        body: JSON.stringify({
          serialNumbers: serialNumbers,
        }),
      });
    } catch (error) {
      throw this.requestError(i18n.global.t("app.cancelSerials.info.nameFull"), error);
    }

    return response.status === 204;
  }

  async fetchBody(url: string, fetchOptions: RequestInit): Promise<{ response: Response; body: unknown }> {
    //request
    let response;
    try {
      response = await fetch(url, fetchOptions);
    } catch (error) {
      if (error instanceof Error && error?.name == "AbortError") throw error;

      throw new RequestError(`Request Failed: ${error instanceof Error ? error.message : String(error)}`);
    }

    if (response?.status === 401) {
      throw new RequestAuthorizationError(`Invalid authorization`);
    }

    //Check status code
    if (!String(response.status).startsWith("2")) {
      throw new RequestError(`Request returned status ${response.status}: ${response.statusText}`);
    }

    //get body
    let body;
    try {
      body = await response.json();
    } catch (error) {
      throw new ResponseError(`Could not parse JSON: ${String(error)}`);
    }

    return { body: body, response: response };
  }

  requestError(requestLocation: string, error: unknown): Error {
    return new Error(
      i18n.global.t(
        error instanceof ResponseError
          ? "global.responseError"
          : error instanceof ResponseParseError
          ? "global.responseParseError"
          : error instanceof RequestAuthorizationError
          ? "global.requestAuthorizationError"
          : "global.requestError",
        {
          url: requestLocation,
        }
      )
    );
  }
}

class RequestError extends Error {
  constructor(message: string) {
    super(message);
  }
}

class ResponseError extends Error {
  constructor(message: string) {
    super(message);
  }
}
class ResponseParseError extends Error {
  constructor(message: string) {
    super(message);
  }
}
class RequestAuthorizationError extends Error {
  constructor(message: string) {
    super(message);
  }
}
